// Account menu — the chip at the bottom of the sidebar plus its dropdown.
// Per spec §10.1: "Click opens dropdown: My Account, Orders, Dwell Points, Logout."
// We omit Dwell Points until that program ships.

import { useEffect, useRef, useState } from 'react';
import { ExternalLink, LogOut, ShoppingBag, User } from 'lucide-react';
import type { CustomerSummary } from '../../lib/types';
import { customerInitials } from '../../lib/format';
import { logout as logoutApi } from '../../lib/api';
import { useStudio, type ViewName } from '../../lib/store';

interface AccountMenuProps {
  customer: CustomerSummary;
  onNavigate: (next: ViewName) => void;
}

export function AccountMenu({ customer, onNavigate }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pushToast = useStudio((s) => s.pushToast);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleLogout() {
    setOpen(false);
    try {
      await logoutApi();
    } catch {
      // Even if the network call fails, force-reload to clear UI state.
    }
    // Hard reload — clears in-memory React Query caches and re-runs the
    // bootstrap auth flow so the AuthGate appears.
    window.location.reload();
  }

  return (
    <div className="account-menu" ref={wrapRef}>
      <button
        type="button"
        className="account-chip"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="account-chip__avatar" aria-hidden="true">
          {customerInitials(customer)}
        </div>
        <div className="account-chip__who">
          <div className="account-chip__name">
            {customer.first_name ? `${customer.first_name} ${customer.last_name ?? ''}`.trim() : customer.email}
          </div>
          <div className="account-chip__sub">{customer.brokerage ?? 'Market Dwellings'}</div>
        </div>
      </button>

      {open && (
        <div className="account-menu__dropdown" role="menu">
          <button
            type="button"
            role="menuitem"
            className="account-menu__item"
            onClick={() => {
              setOpen(false);
              window.location.href = `https://${customer.shopify_shop_domain}/account`;
            }}
          >
            <User size={16} aria-hidden="true" strokeWidth={1.6} />
            <span>My Account</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="account-menu__item"
            onClick={() => {
              setOpen(false);
              onNavigate('orders');
            }}
          >
            <ShoppingBag size={16} aria-hidden="true" strokeWidth={1.6} />
            <span>Orders</span>
          </button>
          <div className="account-menu__sep" />
          {/* Fallback exit affordance — for users who don't think to click
              the sidebar wordmark. Goes back to the brokerage portal home. */}
          <a
            role="menuitem"
            className="account-menu__item"
            href={`https://${customer.shopify_shop_domain}/`}
            onClick={() => setOpen(false)}
          >
            <ExternalLink size={16} aria-hidden="true" strokeWidth={1.6} />
            <span>Market Dwellings home</span>
          </a>
          <div className="account-menu__sep" />
          <button
            type="button"
            role="menuitem"
            className="account-menu__item account-menu__item--danger"
            onClick={() => {
              if (confirm('Sign out of the Studio?')) {
                handleLogout();
              } else {
                pushToast({ type: 'default', message: 'Stayed signed in' });
              }
            }}
          >
            <LogOut size={16} aria-hidden="true" strokeWidth={1.6} />
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );
}
